import React from 'react';
import { Button, Card, Flex, GridAlpha, Heading, Tag, Text } from '@applyboard/crystal-ui';
import { BookOutlineIcon, StarOutlineIcon } from '@applyboard/ui-icons';

import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { formatDateToLocal } from '~/utils';
import { formatMonthYear } from '~/utils/formatDate';
import { TranscriptDocument } from '~/types/transcriptExtraction';
import { resolveAttributeValue, formatTranscriptDate } from '~/utils/transcriptExtraction';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { EducationHistory } from '~/data/useApplication';
import {
  EducationHistoryEditFields,
  EducationHistoryEditData,
} from '../Cards/EducationHistory/components/EducationHistoryEditFields';
import { useGradeSchemeRequired } from '../../data/useGetCustomizedDocumentTypes';

type TranscriptSummarySectionProps = {
  educationHistoryItem?: EducationHistory;
  transcriptDocument?: TranscriptDocument;
  isEditing?: boolean;
  editedData?: EducationHistoryEditData;
  isSaving?: boolean;
  onEdit?: () => void;
  onChange?: (field: keyof EducationHistoryEditData, value: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
};

export function TranscriptSummarySection({
  educationHistoryItem,
  transcriptDocument,
  isEditing = false,
  editedData,
  isSaving = false,
  onEdit,
  onChange,
  onSave,
  onCancel,
}: Readonly<TranscriptSummarySectionProps>) {
  const {
    application: { educationHistories, gpaCalculations },
    educationHistoryTimestamps,
  } = useAppProcessing();
  const gradeSchemeRequired = useGradeSchemeRequired();

  const primaryEducationHistoryItem = educationHistoryItem ?? educationHistories.educationHistoryItems[0];

  if (!primaryEducationHistoryItem) return null;

  // Get GPA calculations for this education history
  const gpas = gpaCalculations?.[primaryEducationHistoryItem.key] || [];

  const transcript =
    transcriptDocument ?? (primaryEducationHistoryItem.transcriptExtractions?.[0] as TranscriptDocument | undefined);

  const attributes = transcript?.attributes;

  const schoolName =
    (attributes && resolveAttributeValue(attributes.schoolName)) || primaryEducationHistoryItem.schoolName;

  const credentialGained =
    (attributes && resolveAttributeValue(attributes.credentialReceived)) ||
    primaryEducationHistoryItem.credentialReceived;

  const educationLevel = primaryEducationHistoryItem.level.name;

  const educationCountry = primaryEducationHistoryItem.country.countryLabel;

  const fieldOfStudy = attributes && resolveAttributeValue(attributes.fieldOfStudy);
  const degree = attributes && resolveAttributeValue(attributes.degree);

  const gradeSchemeName = attributes && resolveAttributeValue(attributes.gradeSchemeName);
  const gradeSchemeValue = gradeSchemeName && gradeSchemeName.trim() ? gradeSchemeName : 'undefined';

  const rawStartDate =
    (attributes && resolveAttributeValue(attributes.startDate)) || primaryEducationHistoryItem.startDate;
  const rawEndDate =
    (attributes && (resolveAttributeValue(attributes.degreeEndDate) || resolveAttributeValue(attributes.endDate))) ||
    primaryEducationHistoryItem.endDate;

  const formatDateValue = (value: unknown): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') {
      if (value.includes('-')) {
        return formatTranscriptDate(value);
      }
      return value;
    }
    if (value instanceof Date) {
      return formatMonthYear(value);
    }
    try {
      return formatMonthYear(new Date(String(value)));
    } catch {
      return String(value);
    }
  };

  const educationStart = formatDateValue(
    typeof rawStartDate === 'string' && rawStartDate.includes('-') ? rawStartDate : new Date(rawStartDate)
  );
  const educationEnd = formatDateValue(
    typeof rawEndDate === 'string' && rawEndDate.includes('-') ? rawEndDate : new Date(rawEndDate)
  );

  const summaryFields: Array<{ label: string; value?: string }> = [
    { label: 'Name of Institution', value: schoolName },
    { label: 'Credential Gained', value: credentialGained },
    { label: 'Education Country', value: educationCountry },
    { label: 'Education Level', value: educationLevel },
    { label: 'Field of Study', value: fieldOfStudy },
    { label: 'Degree', value: degree },
    { label: 'Grade Scheme', value: gradeSchemeValue },
    { label: 'Education Start', value: educationStart },
    { label: 'Education End (or expected)', value: educationEnd },
  ];

  const extractedCourses = attributes?.courses;
  const courseCount = extractedCourses?.length ?? 0;

  const overallTotal = attributes?.overallTotal?.extractedValue;
  const overallGpa = overallTotal?.gpa ?? overallTotal?.grade ?? overallTotal?.obtained;

  const confidenceLevel =
    attributes?.schoolName?.extractedValueConfidence ||
    attributes?.degree?.extractedValueConfidence ||
    attributes?.educationLevel?.extractedValueConfidence;

  return (
    <Flex direction="column" gap={4}>
      {gpas.length > 0 && (
        <Flex gap={4}>
          {gpas.map((calc) => (
            <GpaCalculationCard key={calc.calculatorId} title={calc.calculatorName} gpa={calc.gpa} />
          ))}
        </Flex>
      )}
      <Flex justify="between" align="center">
        <Flex direction="column" gap={1}>
          <Heading level={3}>
            Education History - {attributes?.schoolName?.extractedValue ?? primaryEducationHistoryItem.schoolName}
          </Heading>
          {(() => {
            const docId = transcript?.id ?? primaryEducationHistoryItem.documents?.[0]?.key;
            const ehTimestamp = docId ? educationHistoryTimestamps?.[docId] : undefined;
            if (!ehTimestamp?.updatedAt || isNaN(new Date(ehTimestamp.updatedAt).getTime())) return null;
            return (
              <Text variant="bodyS" contrast="mid">
                Updated on {formatDateToLocal(new Date(ehTimestamp.updatedAt))}
                {ehTimestamp.updatedByRole ? ` by ${ehTimestamp.updatedByRole}` : ''}
              </Text>
            );
          })()}
        </Flex>
        {!isEditing && onEdit && (
          <Button size="sm" variant="outlined" onClick={onEdit}>
            Edit
          </Button>
        )}
      </Flex>
      <Flex gap={6} align="center">
        {courseCount > 0 && (
          <Flex gap={2} align="center">
            <BookOutlineIcon size="md" />
            <Text variant="bodyM">{courseCount} Courses</Text>
          </Flex>
        )}
        {overallGpa && (
          <Flex gap={2} align="center">
            <StarOutlineIcon size="md" />
            <Text variant="bodyM">Overall GPA: {overallGpa}</Text>
          </Flex>
        )}
        {confidenceLevel && (
          <Tag icon="fill" intent={confidenceLevel === 'high' ? 'positive' : 'negative'} size="sm">
            {confidenceLevel === 'high' ? 'High Confidence' : 'Low Confidence'}
          </Tag>
        )}
      </Flex>

      {isEditing && editedData && onChange && onSave && onCancel ? (
        <Flex direction="column" gap={6}>
          <EducationHistoryEditFields
            editedData={editedData}
            onChange={onChange}
            gradeScheme={{ enabled: true, required: gradeSchemeRequired }}
          />
          <Flex gap={4} justify="end">
            <Button size="sm" variant="outlined" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button size="sm" intent="primary" onClick={onSave} loading={isSaving}>
              Save
            </Button>
          </Flex>
        </Flex>
      ) : (
        <GridAlpha rows="repeat(2, auto)" columnGap={2} rowGap={6} columns="repeat(4, 1fr)">
          {summaryFields.map((field) => (
            <GridAlpha.Item key={field.label}>
              <LabelValue label={field.label} value={field.value || '--'} />
            </GridAlpha.Item>
          ))}
        </GridAlpha>
      )}
    </Flex>
  );
}

function GpaCalculationCard({ title, gpa }: { title: string; gpa: number }) {
  return (
    <Flex.Item width="fill">
      <Card emphasis="highlighted">
        <Flex direction="column" align="center">
          <Text variant="labelL" contrast="mid">
            {title}
          </Text>
          <Text variant="titleL" intent="primary">
            {gpa.toFixed(2)}
          </Text>
        </Flex>
      </Card>
    </Flex.Item>
  );
}
